import { Popper } from "@mui/material";
import { StorageLocationTableRow } from "@redotech/redo-fulfillment-wms-common/schema/storage-location";
import OverflowIcon from "@redotech/redo-icons/layers-two-01.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { OverflowTooltip } from "@redotech/redo-web/arbiter-components/tooltip/overflow-tooltip";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useRef, useState } from "react";
import { LotBadge } from "../shared/lot-badge";
import * as styles from "./storage-location-item-cell.module.css";

type InventoryItem = StorageLocationTableRow["inventoryItems"][number];

export const StorageLocationItemCell = memo(function StorageLocationItemCell({
  items,
}: {
  items: InventoryItem[];
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );
  const timeoutRef = useRef<NodeJS.Timeout>();

  if (items.length === 0) {
    return (
      <Flex p="lg">
        <Text fontSize="xs" textColor="secondary">
          -
        </Text>
      </Flex>
    );
  }

  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <>
      <Flex
        align="center"
        dir="row"
        justify="space-between"
        minW="none"
        onMouseEnter={() => {
          if (items.length > 1) {
            timeoutRef.current = setTimeout(() => {
              setIsDropdownOpen(true);
            }, 700);
          }
        }}
        onMouseLeave={() => {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          setIsDropdownOpen(false);
        }}
        overflow="hidden"
        p="lg"
        ref={setDropdownAnchor}
      >
        {items.length > 1 ? (
          <Flex align="center" gap="md">
            <Text fontSize="xs" textColor="secondary">
              {items.length} products ({totalQuantity} qty)
            </Text>
          </Flex>
        ) : (
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          <StorageLocationItemInfo item={items[0]} showQuantity />
        )}
        {items.length > 1 && (
          <RedoButton
            aria-label="More items"
            disabled
            hierarchy="tertiary"
            IconLeading={OverflowIcon}
            onClick={(event) => {
              event.stopPropagation();
            }}
            size="sm"
            theme="normal"
          />
        )}
      </Flex>
      {items.length > 1 && (
        <Popper anchorEl={dropdownAnchor} open={isDropdownOpen}>
          <div className={styles.dropdown}>
            <Flex flexDirection="column" gap="sm" p="md">
              {items.map((item, index) => (
                <Flex
                  align="center"
                  gap="md"
                  key={`${item.productInfo?.id ?? "no-product"}-${index}`}
                  p="sm"
                >
                  <StorageLocationItemInfo item={item} showQuantity />
                </Flex>
              ))}
            </Flex>
          </div>
        </Popper>
      )}
    </>
  );
});

export const StorageLocationItemInfo = memo(function StorageLocationItemInfo({
  item,
  showQuantity = false,
}: {
  item: InventoryItem;
  showQuantity?: boolean;
}) {
  const {
    productInfo,
    inventoryItemKind,
    inventoryItemId,
    quantity,
    lotNumber,
    expirationDate,
  } = item;
  const title =
    productInfo?.title ??
    (inventoryItemKind === "RETURN" ? "Return item" : "Inventory item");
  const imageUrl = productInfo?.imageUrl;
  const sku =
    productInfo?.sku ??
    (!productInfo && inventoryItemId ? inventoryItemId : undefined);

  const [titleRef, setTitleRef] = useState<HTMLDivElement | null>(null);

  const subtextParts: string[] = [];
  if (sku) {
    subtextParts.push(sku);
  }
  if (showQuantity) {
    subtextParts.push(`${quantity} qty`);
  }
  const subtext = subtextParts.join(" · ");

  const expirationText = expirationDate
    ? `Exp: ${new Date(expirationDate).toLocaleString("default", { month: "short", day: "numeric", year: "numeric" })}`
    : null;

  return (
    <Flex align="center" gap="md" minW="none" overflow="hidden">
      <Flex
        className={styles.imageWrapper}
        flexDirection="column"
        justify="center"
      >
        {imageUrl ? (
          <img className={styles.productImage} src={imageUrl} />
        ) : (
          <div className={styles.imagePlaceholder} />
        )}
      </Flex>
      <Flex justify="space-between" minW="none" overflow="hidden" w="full">
        <Flex
          className={styles.truncateContainer}
          flexDirection="column"
          gap="xxs"
          justify="center"
        >
          <OverflowTooltip overflowRef={titleRef} tooltipProps={{ title }}>
            <Text
              className={styles.truncateText}
              fontSize="xs"
              fontWeight="medium"
              ref={setTitleRef}
            >
              {title}
            </Text>
          </OverflowTooltip>
          {subtext && (
            <Text fontSize="xxs" textColor="secondary">
              {subtext}
            </Text>
          )}
          {(lotNumber || expirationText) && (
            <Flex align="center" gap="xs">
              {lotNumber && <LotBadge lotNumber={lotNumber} />}
              {expirationText && (
                <Text fontSize="xxs" textColor="secondary">
                  {expirationText}
                </Text>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});
